/**
 * Lifetime maths for the `cleanlist_ext_jwt` cookie the extension reads.
 *
 * The extension's auth ladder prefers this cookie precisely because it should
 * outlive the portal tab (see cleanlist-extension/src/background/auth-broker).
 * That only holds if the cookie's max-age matches the LIFETIME OF THE TOKEN
 * INSIDE IT, and if we rewrite it before that token expires.
 *
 * Both were wrong. The cookie was pinned to 30 days regardless of its
 * contents, and refreshed on a fixed 10-minute interval. With Clerk's default
 * session token (60 seconds) that produced a 30-day cookie carrying a
 * credential that died after a minute, rewritten long after it had already
 * gone stale — so the extension rejected it as expired, fell through to the
 * rung that needs an open portal tab, and asked the user to reconnect.
 */

const COOKIE_NAME = 'cleanlist_ext_jwt';

const jwtPayload = (token: string): Record<string, unknown> | null => {
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const payload: unknown = JSON.parse(
      atob(segment.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return payload && typeof payload === 'object'
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

/** Seconds remaining on a JWT, or null if it cannot be read. */
export const jwtSecondsRemaining = (
  token: string,
  nowMs: number = Date.now()
): number | null => {
  const exp = jwtPayload(token)?.exp;
  if (typeof exp !== 'number') return null;
  return Math.floor(exp - nowMs / 1000);
};

/** `user:org` for a token, read the way the extension's auth broker reads it. */
export const tokenIdentity = (token: string): string | null => {
  const payload = jwtPayload(token);
  if (typeof payload?.sub !== 'string') return null;
  const nested = payload.o as { id?: unknown } | null | undefined;
  const org =
    typeof payload.org_id === 'string'
      ? payload.org_id
      : typeof nested?.id === 'string'
        ? nested.id
        : '';
  return `${payload.sub}:${org}`;
};

/** The extension cookie on this host, if there is one. */
export const readExtensionCookie = (): string | null => {
  const prefix = `${COOKIE_NAME}=`;
  const entry = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(prefix));
  return entry?.slice(prefix.length) || null;
};

/** Sets the extension cookie on this host; a max-age of 0 deletes it. */
export const saveExtensionCookie = (value: string, maxAgeSeconds: number) => {
  const secure = window.location.protocol === 'https:' ? ' secure;' : '';
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${maxAgeSeconds};${secure} samesite=lax`;
};

/**
 * A tab's first write always goes through. Its later refreshes only renew the
 * login already there, so a tab left on another org can't switch it back.
 */
export const shouldWriteExtensionCookie = ({
  existing,
  next,
  renewalOnly,
  nowMs = Date.now(),
}: {
  existing: string | null;
  next: string;
  renewalOnly: boolean;
  nowMs?: number;
}): boolean => {
  if (!renewalOnly || !existing) return true;
  const left = jwtSecondsRemaining(existing, nowMs);
  if (left === null || left <= 0) return true;
  return tokenIdentity(existing) === tokenIdentity(next);
};

/**
 * Cookie max-age: exactly what the token is worth, never more. A token we
 * cannot read gets no cookie at all (0) rather than a long-lived one wrapped
 * around something unverifiable.
 */
export const cookieMaxAgeSeconds = (
  token: string,
  nowMs: number = Date.now()
): number => Math.max(0, jwtSecondsRemaining(token, nowMs) ?? 0);

/** Never let the cookie lapse: rewrite this many seconds before expiry. */
export const SAFETY_MARGIN_SECONDS = 15;
/** Don't spin, even when the token is nearly dead. */
export const REFRESH_FLOOR_MS = 2_000;
export const REFRESH_CEILING_MS = 10 * 60 * 1000;

/**
 * Half-life, but never later than `SAFETY_MARGIN_SECONDS` before expiry.
 *
 * Half-life alone is correct in the middle and WRONG at the tail: with a 60s
 * token each rewrite stores the same still-valid token from Clerk's client
 * cache with a shorter max-age, so the delay converges on the floor — and a
 * floor larger than the life remaining schedules the rewrite AFTER the cookie
 * has already expired. Observed live: the cookie vanished for ~10s in every
 * cycle, which is long enough for the extension to walk its ladder, find
 * nothing, and ask the user to reconnect.
 */
export const refreshDelayMs = (
  token: string,
  nowMs: number = Date.now()
): number => {
  const remaining = jwtSecondsRemaining(token, nowMs);
  // Unreadable or already dead: retry promptly rather than stopping, so a
  // transient decode failure cannot permanently strand the extension.
  if (remaining === null || remaining <= 0) return REFRESH_FLOOR_MS;
  // Less life left than the floor: refresh NOW. Applying the floor here
  // scheduled the rewrite after expiry, and the expired rewrite stored a
  // zero max-age — removing the cookie instead of renewing it (review).
  if (remaining * 1000 <= REFRESH_FLOOR_MS) return 0;
  const half = (remaining * 1000) / 2;
  const beforeExpiry = (remaining - SAFETY_MARGIN_SECONDS) * 1000;
  return Math.min(
    REFRESH_CEILING_MS,
    Math.max(REFRESH_FLOOR_MS, Math.min(half, beforeExpiry))
  );
};
